import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type User } from "@/lib/api";

const TERMS_KEY = "otium_google_terms";

export function GoogleButton({ accepted, onError, back, testId }: { accepted?: boolean; onError?: (m: string) => void; back: string; testId: string }) {
  const go = () => {
    if (accepted === false) return onError?.("Acepta los Términos y el Aviso de privacidad para crear tu cuenta");
    sessionStorage.setItem(TERMS_KEY, accepted ? "1" : "0");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + back;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} className="flex h-11 w-full items-center justify-center gap-3 rounded-md border border-[#DDE1E8] bg-white font-semibold text-[#1B2A4A] transition-colors hover:bg-slate-50" data-testid={testId}>
      <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      Continuar con Google
    </button>
  );
}

export function GoogleCallback({ hash }: { hash: string }) {
  const { adopt } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(hash.slice(1)).get("session_id") ?? "";
    const accepted = sessionStorage.getItem(TERMS_KEY) === "1";
    sessionStorage.removeItem(TERMS_KEY);
    api.post<User>("/auth/google", { session_id: sessionId, accepted_terms: accepted })
      .then(({ data }) => {
        adopt(data);
        navigate(data.billing?.access === "none" ? "/suscripcion?auto=1" : "/resumen", { replace: true });
      })
      .catch((e) => setError(formatApiError(e)));
  }, [hash, adopt, navigate]);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FAF7F1] px-6 text-center" data-testid="google-callback">
      {error ? (
        <>
          <p className="max-w-sm text-[#9C3D24]" data-testid="google-callback-error">{error}</p>
          <button type="button" onClick={() => navigate("/registro", { replace: true })} className="font-semibold text-[#1B2A4A] underline" data-testid="google-callback-back">Volver al registro</button>
        </>
      ) : <Loader2 className="h-8 w-8 animate-spin text-[#2C4F91]" />}
    </div>
  );
}
